import CoupeGeologique from "./CoupeGeologique";
import { normaliserZone } from "../utils/normalisation";
import { nettoyerReponsePourRapport } from "../helpers/nettoyageRapport";

const FORMAT_NIVEAU = {
  "TRÈS_ADAPTÉE":          "Très adaptée",
  "ADAPTÉE":               "Adaptée",
  "ADAPTÉE_AVEC_RÉSERVES": "Adaptée avec réserves",
  "PEU_ADAPTÉE":           "Peu adaptée",
  "COMPLÉMENTAIRE":        "Complémentaire",
};
const formatNiveau = (niveau) => FORMAT_NIVEAU[niveau] || null;

const LABEL_TYPE_CLIENT = {
  maitre_ouvrage: "Maître d'ouvrage",
  bureau_etudes:  "Bureau d'études",
  entreprise_tp:  "Entreprise de travaux",
  collectivite:   "Collectivité",
  particulier:    "Particulier",
};

// ═══════════════════════════════════════════════════════
// RAPPORT PROJET — Rapport technique ~2 pages A4
// Sections : Fiche, Description, Terrain, Contraintes, Analyse
// ═══════════════════════════════════════════════════════

const LABEL_OUVRAGE = {
  soutenement_remblai:    "Soutènement de remblai",
  soutenement_deblai:     "Soutènement de déblai",
  talus_stabilisation:    "Stabilisation de talus",
  fondation_batiment:     "Fondation de bâtiment",
  infrastructure_lineaire:"Infrastructure linéaire",
  ouvrage_art:            "Ouvrage d'art",
};

const LABEL_ZONE = {
  "I":  "Zone 1 — très faible sismicité (ag = 0.040g)",
  "II": "Zone 2 — faible sismicité (ag = 0.070g)",
  "III":"Zone 3 — sismicité modérée (ag = 0.110g)",
  "IV": "Zone 4 — sismicité moyenne (ag = 0.160g)",
  "V":  "Zone 5 — forte sismicité (ag = 0.300g)",
};

const LABEL_NAPPE = {
  nulle:       "Absente",
  basse:       "Basse — non influente",
  moderee:     "Modérée — à prendre en compte",
  saisonniere: "Saisonnière — fluctuante",
  haute:       "Haute — influente",
};

const LABEL_SURCHARGE = {
  "0":  "Aucune surcharge",
  "10": "10 kN/m² — trafic léger",
  "20": "20 kN/m² — trafic moyen",
  "30": "30 kN/m² — zone industrielle",
  "50": "50 kN/m² — stockage lourd",
};

const LABEL_ESPACE = {
  libre:         "Libre (≥ 0.7 × H)",
  contraint:     "Contraint (0.3–0.7 × H)",
  tres_contraint:"Très contraint (< 0.3 × H)",
  limite:        "Limité",
};

const NATURE_LABEL = {
  remblai:"Remblai", argile:"Argile", limon:"Limon",
  sable:"Sable", gravier:"Gravier", marne:"Marne",
  roche:"Roche", terre_vegetale:"Terre végétale", default:"Sol"
};

const formatSol = (sol = "") => sol
  .replace(/_/g, " ")
  .replace(/\b\w/g, l => l.toUpperCase());

// ── Palette sobre pour impression ──────────────────────
const C = {
  bleu:    "#1a3a5c",
  bleuCl:  "#2563eb",
  vert:    "#15803d",
  gris:    "#6b7280",
  grisCl:  "#f3f4f6",
  border:  "#d1d5db",
  rouge:   "#b91c1c",
  orange:  "#d97706",
  jaune:   "#fef9c3",
};

// ─────────────────────────────────────────────────────────
function SectionTitre({ num, titre }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:10,
      borderBottom:`2px solid ${C.bleu}`, marginBottom:14, paddingBottom:6,
    }}>
      <div style={{
        background:C.bleu, color:"#fff", borderRadius:"50%",
        width:22, height:22, display:"flex", alignItems:"center",
        justifyContent:"center", fontSize:11, fontWeight:900, flexShrink:0,
      }}>{num}</div>
      <div style={{ fontSize:13, fontWeight:800, color:C.bleu, letterSpacing:0.5, textTransform:"uppercase" }}>
        {titre}
      </div>
    </div>
  );
}

function ChampInfo({ label, valeur, highlight }) {
  if (!valeur && valeur !== 0) return null;
  return (
    <div style={{ display:"flex", gap:6, fontSize:12, padding:"4px 0", borderBottom:`1px solid ${C.border}` }}>
      <span style={{ color:C.gris, minWidth:160, flexShrink:0 }}>{label}</span>
      <span style={{ fontWeight: highlight ? 700 : 500, color: highlight ? C.bleu : "#1a1a1a" }}>{valeur}</span>
    </div>
  );
}

// ── Helpers regroupement §6 ──────────────────────────────
const construireCleGroupement = (excl) => {
  const reglesIds = (excl.regles_resolues || [])
    .map(r => r.id).sort().join("|");
  const motifsText = (excl.motifs || []).slice().sort().join("|");
  return `${reglesIds}::${motifsText}`;
};

const grouperExclusions = (exclues) => {
  if (!exclues || exclues.length === 0) return [];
  const groupes = [];
  exclues.forEach(excl => {
    const cle = construireCleGroupement(excl);
    let groupe = groupes.find(g => g.cle === cle);
    if (!groupe) {
      groupe = {
        cle,
        regles_resolues: excl.regles_resolues || [],
        regles:          excl.regles          || [],
        motifs:          excl.motifs          || [],
        solutions:       [],
      };
      groupes.push(groupe);
    }
    groupe.solutions.push(excl.nom);
  });
  return groupes;
};

// ─────────────────────────────────────────────────────────
const parseAlerteMinimale = (texte) => {
  if (!texte) return { id: null, texte: "" };
  const idMatch = texte.match(/^\[([A-Z0-9]+-?\d+)\]/);
  const id = idMatch ? idMatch[1] : null;
  const sansEmoji = texte
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u{2600}-\u{27BF}]/gu, "");
  const sansId = id
    ? sansEmoji.replace(/^\s*\[[A-Z0-9]+-?\d+\]\s*/, "")
    : sansEmoji;
  return { id, texte: sansId.trim() };
};

// ─────────────────────────────────────────────────────────
function RapportProjet({ donneesProjet, solutions = [], alertesProjet = [], excluesProjet = [], onNavigate, sectionsActives = {}, onSupprDialogueRapport = () => {}, diagnosticBackend = null }) {
  if (!donneesProjet) return null;

  const {
    clientInfo, typeOuvrage, hauteur, longueur,
    typeSol, frottement, rdt, couches = [],
    zoneSismique, nappe, nappeProf,
    surcharge, espaceDisponible, description, zoneUsage,
  } = donneesProjet;

  const today = new Date().toLocaleDateString("fr-FR", {
    day:"2-digit", month:"long", year:"numeric"
  });
  const logoClient = localStorage.getItem("logo_client");

  const labelOuvrage = LABEL_OUVRAGE[typeOuvrage] || typeOuvrage || "—";
  const labelZone    = LABEL_ZONE[zoneSismique]    || (zoneSismique ? `Zone ${zoneSismique}` : "Non renseignée");
  const labelNappe   = LABEL_NAPPE[nappe]          || nappe || "Non renseignée";
  const labelSurch   = LABEL_SURCHARGE[surcharge]  || (surcharge ? `${surcharge} kN/m²` : "Non renseignée");
  const labelEspace  = LABEL_ESPACE[espaceDisponible] || espaceDisponible || "Non renseigné";

  // Nature sol principale (hors terre végétale / remblai)
  const couchePrincipale = couches.find(c =>
    !["terre_vegetale","remblai"].includes(c.nature)
  ) || couches[0];
  const natureSol = couchePrincipale
    ? (NATURE_LABEL[couchePrincipale.nature] || formatSol(couchePrincipale.nature))
    : (typeSol ? formatSol(typeSol) : "—");

  const best = solutions[0];

  const nappeActive    = nappe && nappe !== "nulle" && nappe !== "basse";
  const zoneNorm       = normaliserZone(zoneSismique);
  const seismiqueEleve = ["III","IV","V","VI"].includes(zoneNorm);

  // P2b2.3 — valeurs normatives depuis backend (fallback hardcodé si bloc absent)
  const rapportCoeff   = diagnosticBackend?.rapport?.coefficients_ec7;
  const rapportSismiq  = diagnosticBackend?.rapport?.sismique;
  const gammaG  = rapportCoeff?.gamma_G   ?? 1.35;
  const gammaQ  = rapportCoeff?.gamma_Q   ?? 1.50;
  const gammaRh = rapportCoeff?.gamma_R_h ?? 1.10;
  const gammaRv = rapportCoeff?.gamma_R_v ?? 1.40;
  const gammaRe = rapportCoeff?.gamma_R_e ?? 1.40;

  const labelHauteur = typeOuvrage?.startsWith("fondation") ? "Profondeur d'ancrage"
                     : typeOuvrage?.startsWith("talus")     ? "Hauteur du talus"
                     : "Hauteur de soutènement";

  const normeRef = typeOuvrage?.startsWith("fondation")      ? "NF P94-261 / EC7 DA2*"
                 : typeOuvrage?.startsWith("soutenement")    ? "NF P94-281 / NF P94-282 / EC7"
                 : typeOuvrage?.startsWith("talus")          ? "NF EN 1997-1 (Eurocode 7) / NF EN 14475"
                 : typeOuvrage?.startsWith("amelioration")   ? "NF EN 1997-1 / Recommandations CFMS"
                 : typeOuvrage?.startsWith("infrastructure") ? "NF EN 1997-1 / NF P94-261 / NF P94-262"
                 : "NF P94-500";

  return (
    <div style={{ marginBottom:32, fontFamily:"'Segoe UI', sans-serif" }}>

      <div
        id="rapport-contenu"
        style={{
          background:"#fff",
          border:`1.5px solid ${C.border}`,
          borderRadius:12,
          padding:"32px 36px",
          boxShadow:"0 4px 20px rgba(0,0,0,0.08)",
          maxWidth:900,
        }}
      >

        {/* ══ EN-TÊTE ══════════════════════════════════════════ */}
        <div style={{
          display:"flex", justifyContent:"space-between", alignItems:"flex-start",
          borderBottom:`3px solid ${C.bleu}`, paddingBottom:18, marginBottom:24,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <img src="/assets/logo_geoexpert.png" alt="GeoExpert" style={{ height:72, objectFit:"contain" }}/>
            <div>
              <div style={{ fontSize:22, fontWeight:900, color:C.bleu, marginBottom:4 }}>
                Rapport d'Analyse Géotechnique
              </div>
              <div style={{ fontSize:13, color:C.gris }}>
                Logiciel expert GEO5 · Norme EC7 DA2* · NF P94 · EC8
              </div>
            </div>
          </div>
          <div style={{ textAlign:"right", fontSize:11, color:C.gris, lineHeight:1.8 }}>
            {logoClient && (
              <img src={logoClient} alt="Logo client" style={{ height:40, objectFit:"contain", marginBottom:8, display:"block", marginLeft:"auto" }}/>
            )}
            <div style={{ fontWeight:700, color:C.bleu, fontSize:13 }}>
              {clientInfo?.codeRef || "Référence N/A"}
            </div>
            <div>Date : {today}</div>
            <div>Établi avec GeoExpert FR · Méthode assistée par IA</div>
          </div>
        </div>

        {/* ══ SYNTHÈSE EXÉCUTIVE ══════════════════════════════ */}
        <div style={{
          background:"#eff6ff", border:"1px solid #bfdbfe",
          borderRadius:8, padding:"14px 18px", marginBottom:20,
          pageBreakInside:"avoid",
        }}>
          <div style={{ fontSize:13, fontWeight:800, color:C.bleu, marginBottom:10 }}>
            Synthèse exécutive
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 24px", fontSize:12 }}>
            <div>
              <span style={{ color:C.gris }}>Ouvrage :</span>{" "}
              <strong>{labelOuvrage}</strong>
              {hauteur && <> — H = {hauteur} m</>}
            </div>
            <div>
              <span style={{ color:C.gris }}>Sol principal :</span>{" "}
              <strong>{natureSol}</strong>
              {frottement && <>, φ' = {frottement}°</>}
            </div>
            {best && (
              <div style={{ gridColumn:"1/-1", marginTop:4 }}>
                <span style={{ color:C.gris }}>Solution recommandée :</span>{" "}
                <strong style={{ color:C.bleu }}>{best.nom}</strong>
                {formatNiveau(best.niveau) && <> — {formatNiveau(best.niveau)}</>}
              </div>
            )}
            <div>
              <span style={{ color:C.gris }}>Solutions analysées :</span>{" "}
              <strong>{solutions.length}</strong>
              {excluesProjet.length > 0 && (
                <> ({excluesProjet.length} non retenue{excluesProjet.length > 1 ? "s" : ""} par règles)</>
              )}
            </div>
            {alertesProjet.length > 0 && (
              <div>
                <span style={{ color:C.rouge }}>Alertes normatives :</span>{" "}
                <strong style={{ color:C.rouge }}>
                  {alertesProjet.length} règle{alertesProjet.length > 1 ? "s" : ""} déclenchée{alertesProjet.length > 1 ? "s" : ""}
                </strong>
              </div>
            )}
          </div>
        </div>

        {/* ══ SECTION 1 — FICHE PROJET ════════════════════════ */}
        <div style={{ marginBottom:24 }}>
          <SectionTitre num="1" titre="Fiche Projet" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 32px" }}>
            <div>
              <ChampInfo label="Maître d'ouvrage"    valeur={clientInfo?.nomEntreprise} highlight />
              <ChampInfo label="Donneur d'ordre"      valeur={LABEL_TYPE_CLIENT[clientInfo?.typeClient] || clientInfo?.typeClient} />
              <ChampInfo label="Contact"             valeur={[clientInfo?.prenomContact, clientInfo?.nomContact].filter(Boolean).join(" ") || null} />
              <ChampInfo label="Téléphone"           valeur={clientInfo?.mobile} />
              <ChampInfo label="Budget estimé"       valeur={clientInfo?.budget ? `${parseFloat(clientInfo.budget).toLocaleString("fr-FR")} €` : null} />
            </div>
            <div>
              <ChampInfo label="Référence projet"    valeur={clientInfo?.codeRef} highlight />
              <ChampInfo label="Localisation"        valeur={clientInfo?.wilaya?.nom || clientInfo?.localisation} />
              <ChampInfo label="Date d'analyse"      valeur={today} />
              {clientInfo?.dateModification && (
                <ChampInfo label="Dernière modification"
                  valeur={new Date(clientInfo.dateModification).toLocaleDateString("fr-FR",{ day:"2-digit", month:"short", year:"numeric" })} />
              )}
            </div>
          </div>
        </div>

        {/* ══ MÉTHODOLOGIE ════════════════════════════════════ */}
        <div style={{
          background:C.grisCl, border:`1px solid ${C.border}`,
          borderRadius:6, padding:"10px 14px", marginBottom:20,
          fontSize:11.5, color:"#374151", lineHeight:1.7,
          pageBreakInside:"avoid",
        }}>
          <span style={{ fontWeight:700, color:C.bleu }}>Méthodologie :</span>{" "}
          L'analyse s'appuie sur les règles expertes du moteur GeoExpert FR — critères
          géométriques, géotechniques et normatifs issus de NF P94-500, EC7 DA2* et EC8.
          Le classement des solutions procède par filtrage d'éligibilité puis par adéquation
          multi-critères. Les logiciels GEO5 identifiés correspondent aux calculs de
          dimensionnement à réaliser en phase G2 PRO (NF P94-500).
        </div>

        {/* ══ SECTION 2 — DESCRIPTION DU PROJET ══════════════ */}
        {sectionsActives.description !== false && (
        <div style={{ marginBottom:24 }}>
          <SectionTitre num="2" titre="Description du Projet" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 32px" }}>
            <div>
              <ChampInfo label="Type d'ouvrage"     valeur={labelOuvrage} highlight />
              <ChampInfo label={labelHauteur} valeur={hauteur ? `${hauteur} m` : null} />
              <ChampInfo label="Longueur d'ouvrage" valeur={longueur ? `${longueur} m` : null} />
              <ChampInfo label="Usage / contexte"   valeur={zoneUsage || description || null} />
            </div>
            <div>
              <ChampInfo label="Sol de fondation"   valeur={natureSol} highlight />
              <ChampInfo label="Angle de frottement φ'" valeur={frottement ? `${frottement}°` : null} />
              <ChampInfo label="Résistance de pointe" valeur={rdt ? `${rdt} kPa` : null} />
            </div>
          </div>
        </div>
        )}

        {/* ══ SECTION 3 — DONNÉES TERRAIN ══════════════════════ */}
        {sectionsActives.terrain !== false && (
        <div style={{ marginBottom:24 }}>
          <SectionTitre num="3" titre="Données Terrain — Stratigraphie" />

          {couches.length > 0 ? (
            <div style={{ display:"flex", gap:24, alignItems:"flex-start" }}>

              {/* Coupe géologique SVG — réduite à 180px pour PDF */}
              <div style={{ flexShrink:0, width:105, height:180, overflow:"hidden" }}>
                <div style={{ transform:"scale(0.375)", transformOrigin:"top left" }}>
                  <CoupeGeologique
                    couches={couches}
                    nappe={nappe && nappe !== "nulle"}
                    nappe_profondeur={parseFloat(nappeProf) || 99}
                  />
                </div>
              </div>

              {/* Tableau des couches */}
              <div style={{ flex:1, overflowX:"auto" }}>
                <table style={{
                  width:"100%", borderCollapse:"collapse",
                  fontSize:11.5, tableLayout:"fixed",
                }}>
                  <thead>
                    <tr style={{ background:C.bleu, color:"#fff" }}>
                      {["Couche","Nature","γ (kN/m³)","φ' (°)","c (kPa)","Prof. toit","Prof. base"].map(h => (
                        <th key={h} style={{ padding:"7px 8px", textAlign:"left", fontWeight:700, borderRight:"1px solid rgba(255,255,255,0.15)" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {couches.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? C.grisCl : "#fff" }}>
                        <td style={{ padding:"6px 8px", fontWeight:600, color:C.bleu }}>C{i+1}</td>
                        <td style={{ padding:"6px 8px" }}>{c.nom || formatSol(c.nature || "")}</td>
                        <td style={{ padding:"6px 8px", textAlign:"center" }}>{c.gamma || "—"}</td>
                        <td style={{ padding:"6px 8px", textAlign:"center" }}>{c.frottement ?? c.phi ?? "—"}</td>
                        <td style={{ padding:"6px 8px", textAlign:"center" }}>{c.cohesion ?? "—"}</td>
                        <td style={{ padding:"6px 8px", textAlign:"center" }}>{c.profondeur_toit ?? 0} m</td>
                        <td style={{ padding:"6px 8px", textAlign:"center" }}>{c.profondeur_base ?? "—"} m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Légende nappe */}
                {nappeActive && (
                  <div style={{
                    marginTop:10, background:"#eff6ff", border:"1px solid #bfdbfe",
                    borderRadius:7, padding:"8px 12px", fontSize:11.5, color:"#1d4ed8",
                  }}>
                    <strong>▼ Nappe phréatique</strong> : {labelNappe}
                    {nappeProf ? ` à ${nappeProf} m de profondeur` : ""}
                  </div>
                )}

                {/* Paramètres EC7 */}
                <div style={{
                  marginTop:10, background:"#f0fdf4", border:"1px solid #bbf7d0",
                  borderRadius:7, padding:"8px 12px", fontSize:11,
                  pageBreakInside:"avoid",
                }}>
                  <div style={{ fontWeight:700, color:C.vert, marginBottom:4 }}>
                    Coefficients partiels EC7 DA2* (France)
                  </div>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap", color:"#166534" }}>
                    <span>γG = {Number(gammaG).toFixed(2)}</span>
                    <span>γQ = {Number(gammaQ).toFixed(2)}</span>
                    <span>γR,h = {Number(gammaRh).toFixed(2)}</span>
                    <span>γR,v = {Number(gammaRv).toFixed(2)}</span>
                    <span>γR,e = {Number(gammaRe).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color:C.gris, fontStyle:"italic", fontSize:12 }}>
              Aucune donnée de couche renseignée.
            </div>
          )}
        </div>
        )}

        {/* ══ SECTION 4 — CONTRAINTES ══════════════════════════ */}
        {sectionsActives.contraintes !== false && (
        <div style={{ marginBottom:24 }}>
          <SectionTitre num="4" titre="Contraintes du Projet" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 32px" }}>
            <div>
              <ChampInfo label="Zone sismique (EC8)" valeur={labelZone} highlight={seismiqueEleve} />
              <ChampInfo label="Nappe phréatique"    valeur={labelNappe} highlight={nappeActive} />
              <ChampInfo label="Surcharge en crête"  valeur={labelSurch} />
            </div>
            <div>
              <ChampInfo label="Emprise disponible"    valeur={labelEspace} highlight={espaceDisponible === "tres_contraint"} />
              <ChampInfo label="Dispositif de drainage" valeur={nappeActive ? "Géotextile + drain requis" : "Standard"} />
              <ChampInfo label="Norme applicable"    valeur={normeRef} />
            </div>
          </div>

          {/* Alertes contextuelles */}
          {(seismiqueEleve || nappeActive || espaceDisponible === "tres_contraint") && (
            <div style={{
              marginTop:12, background:"#fffbeb", border:"1px solid #fcd34d",
              borderRadius:8, padding:"10px 14px", fontSize:12,
            }}>
              <div style={{ fontWeight:700, color:C.orange, marginBottom:6 }}>Points de vigilance</div>
              <ul style={{ margin:0, paddingLeft:18, color:"#78350f", lineHeight:1.8 }}>
                {seismiqueEleve && (
                  <li>
                    Zone sismique {zoneSismique} — vérification pseudo-statique EC8 obligatoire
                    {rapportSismiq?.kh != null && (
                      <> (kh&nbsp;=&nbsp;{rapportSismiq.kh.toFixed(3)} — Hypothèses&nbsp;: ag zone {rapportSismiq.zone_norm || zoneNorm}, {rapportSismiq.kh_hypotheses} — {rapportSismiq.reference})</>
                    )}
                  </li>
                )}
                {nappeActive && <li>Nappe phréatique influente — poussée hydrostatique et drainage à dimensionner</li>}
                {espaceDisponible === "tres_contraint" && <li>Espace très contraint — solutions à emprise réduite à privilégier (écrans, paroi berlinoise)</li>}
              </ul>
            </div>
          )}
        </div>
        )}

        {/* ══ SECTION 5 — ANALYSE & RÉSULTATS ══════════════════ */}
        <div style={{ marginBottom:24, pageBreakAfter:"avoid" }}>
          <SectionTitre num="5" titre="Analyse et Résultats" />

          {/* Résultat principal */}
          {best ? (
            <>
              {/* Solution retenue */}
              <div className="solution-card" style={{
                background:C.bleu, color:"#fff",
                borderRadius:10, padding:"14px 20px", marginBottom:14,
                pageBreakInside:"avoid",
              }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:"uppercase", opacity:0.75, marginBottom:4 }}>
                  Solution Recommandée
                  {(formatNiveau(best.niveau) || best.score != null) && " — "}
                  {formatNiveau(best.niveau) || (best.score != null ? `Score : ${best.score}/100` : "")}
                </div>
                <div style={{ fontSize:18, fontWeight:900, display:"flex", alignItems:"center", gap:10 }}>
                  {best.icone && <span style={{ fontSize:26 }}>{best.icone}</span>}
                  <span>{best.nom}</span>
                </div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
                {/* Avantages */}
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.vert, marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>
                    Éléments favorables
                  </div>
                  <ul style={{ margin:0, paddingLeft:16, fontSize:12, color:"#166534", lineHeight:1.9 }}>
                    {(best.avantages || []).map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
                {/* Inconvénients */}
                <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.orange, marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>
                    Points de vigilance
                  </div>
                  <ul style={{ margin:0, paddingLeft:16, fontSize:12, color:"#78350f", lineHeight:1.9 }}>
                    {(best.inconvenients || []).length > 0
                      ? (best.inconvenients || []).map((inc, i) => <li key={i}>{inc}</li>)
                      : <li style={{ fontStyle:"italic", color:C.gris }}>Aucun point critique identifié</li>
                    }
                  </ul>
                </div>
              </div>

              {/* Modules GEO5 requis */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:8 }}>
                  Logiciels GEO5 mobilisables
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {(best.modules_geo5 || []).map(m => (
                    <span key={m} style={{
                      background:C.bleu, color:"#fff",
                      borderRadius:99, padding:"4px 14px", fontSize:11, fontWeight:600,
                    }}>{m}</span>
                  ))}
                </div>
              </div>

              {/* Solutions alternatives rang 2 et 3 — avec motifs */}
              {sectionsActives.alternative !== false && solutions.slice(1, 3).map((alt, idx) => {
                const positifs = alt.avantages || [];
                const negatifs = [...(alt.motifs_negatifs || []), ...(alt.inconvenients || [])];
                const hasMotifs = positifs.length > 0 || negatifs.length > 0;
                return (
                  <div key={alt.id} style={{
                    background:C.grisCl, border:`1px solid ${C.border}`,
                    borderRadius:9, padding:"12px 16px", marginBottom:14,
                  }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.gris, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>
                      Alternative — rang {idx + 2}
                      {(formatNiveau(alt.niveau) || alt.score != null) && " — "}
                      {formatNiveau(alt.niveau) || (alt.score != null ? `Score : ${alt.score}/100` : "")}
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                      {alt.icone && <span style={{ fontSize:20 }}>{alt.icone}</span>}
                      <span style={{ fontSize:13, fontWeight:700, color:"#1a1a1a" }}>{alt.nom}</span>
                    </div>
                    <div style={{ fontSize:12, color:C.gris, marginBottom: hasMotifs ? 8 : 0 }}>
                      Logiciels : {(alt.modules_geo5 || []).join(" · ")}
                    </div>
                    {positifs.length > 0 && (
                      <div style={{ marginTop:6 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.vert, marginBottom:4 }}>Avantages</div>
                        <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:"#166534", lineHeight:1.8 }}>
                          {positifs.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                    {negatifs.length > 0 && (
                      <div style={{ marginTop:6 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.orange, marginBottom:4 }}>Réserves</div>
                        <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:"#78350f", lineHeight:1.8 }}>
                          {negatifs.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                    {!hasMotifs && alt.niveau && (
                      <div style={{ fontSize:12, color:C.gris, fontStyle:"italic", marginTop:6 }}>
                        Réserves détaillées non disponibles dans le diagnostic courant.
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Tableau comparatif si plusieurs solutions */}
              {sectionsActives.tableau_comparatif !== false && solutions.length >= 2 && (
                <div className="tableau-comparatif" style={{ marginBottom:14, pageBreakInside:"avoid" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:8 }}>
                    Tableau comparatif des solutions
                  </div>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11.5 }}>
                    <thead>
                      <tr style={{ background:C.grisCl }}>
                        {["Solution","Adéquation qualitative","Logiciels GEO5","Rang"].map(h => (
                          <th key={h} style={{
                            padding:"7px 10px", textAlign:"left",
                            fontWeight:700, color:C.bleu, border:`1px solid ${C.border}`,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {solutions.map((s, i) => (
                        <tr key={s.id} style={{ background: i === 0 ? "#eff6ff" : "#fff" }}>
                          <td style={{ padding:"7px 10px", border:`1px solid ${C.border}`, fontWeight: i === 0 ? 700 : 400 }}>
                            {s.icone && <>{s.icone} </>}{s.nom}
                          </td>
                          <td style={{ padding:"7px 10px", border:`1px solid ${C.border}`, textAlign:"center", fontWeight:700,
                            color: s.niveau === "TRÈS_ADAPTÉE" ? C.vert
                                 : s.niveau === "ADAPTÉE"      ? C.bleuCl
                                 : s.score  >= 90              ? C.vert
                                 : s.score  >= 75              ? C.orange
                                 : C.gris }}>
                            {formatNiveau(s.niveau) || (s.score != null ? `${s.score}%` : "—")}
                          </td>
                          <td style={{ padding:"7px 10px", border:`1px solid ${C.border}`, fontSize:11, color:C.gris }}>
                            {(s.modules_geo5 || []).join(", ")}
                          </td>
                          <td style={{ padding:"7px 10px", border:`1px solid ${C.border}`, textAlign:"center",
                            fontWeight: i === 0 ? 800 : 400, color: i === 0 ? C.bleuCl : C.gris }}>
                            {i === 0 ? "★ Recommandée" : `#${i + 1}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Alertes backend — max 3 dans le PDF */}
              {sectionsActives.alertes !== false && alertesProjet.length > 0 && (() => {
                const alertesPDF   = alertesProjet.slice(0, 3);
                const resteAlertes = alertesProjet.length - 3;
                return (
                  <div className="alerte-item" style={{
                    border:`1px solid ${C.border}`, borderRadius:8,
                    padding:"10px 14px", marginBottom:14,
                    pageBreakInside:"avoid", background:"#f9fafb",
                  }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.bleu, marginBottom:6 }}>
                      Points de contrôle normatif — EN 1997 / NF P94
                    </div>
                    <ul style={{ margin:0, paddingLeft:16, fontSize:12, color:"#374151", lineHeight:1.8 }}>
                      {alertesPDF.map((a, i) => {
                        if (typeof a === "string") {
                          const parsed = parseAlerteMinimale(a);
                          return (
                            <li key={i}>
                              {parsed.id && (
                                <span style={{ fontFamily:"monospace", marginRight:6, color:C.gris }}>[{parsed.id}]</span>
                              )}
                              {parsed.texte}
                            </li>
                          );
                        }
                        const alerteTexte = (a.alerte || "")
                          .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
                          .replace(/[\u{2600}-\u{27BF}]/gu, "")
                          .trim();
                        return (
                          <li key={i}>
                            {a.id && (
                              <span style={{ fontFamily:"monospace", marginRight:6, color:C.gris }}>[{a.id}]</span>
                            )}
                            {alerteTexte}
                            {Array.isArray(a.modules) && a.modules.length > 0 && (
                              <span style={{ color:C.bleuCl, marginLeft:6, fontSize:11 }}>
                                — GEO5 : {a.modules.join(", ")}
                              </span>
                            )}
                          </li>
                        );
                      })}
                      {resteAlertes > 0 && (
                        <li
                          style={{ fontStyle:"italic", color:C.bleuCl, marginTop:4, cursor:"pointer", textDecoration:"underline", fontSize:11 }}
                          onClick={() => onNavigate?.("agent")}
                        >
                          + {resteAlertes} autre{resteAlertes > 1 ? "s" : ""} règle{resteAlertes > 1 ? "s" : ""} déclenchée{resteAlertes > 1 ? "s" : ""} — consulter l'Analyse IA pour l'analyse complète
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })()}
            </>
          ) : (
            <div style={{ color:C.gris, fontStyle:"italic", fontSize:12 }}>
              Aucune solution calculée — vérifiez les données saisies.
            </div>
          )}
        </div>

        {/* ══ SECTION 6 — SOLUTIONS NON RETENUES ══════════════ */}
        {sectionsActives.solutions_ecartees !== false && excluesProjet.length > 0 && (() => {
          const groupes = grouperExclusions(excluesProjet);
          if (groupes.length === 0) return null;
          return (
            <div style={{ marginTop:24, marginBottom:24 }}>
              <SectionTitre num="6" titre="Solutions non retenues — analyse d'éligibilité" />
              <p style={{
                fontSize:12, color:C.gris, fontStyle:"italic",
                marginBottom:16, marginTop:8, lineHeight:1.6,
              }}>
                Les solutions suivantes ont été analysées puis écartées sur la base des règles
                d'éligibilité du moteur. Une révision est possible si les conditions de projet
                évoluent.
              </p>
              {groupes.map((groupe, i) => {
                const hasRR   = groupe.regles_resolues.length > 0;
                const hasRegl = groupe.regles.length > 0;
                const hasMotf = groupe.motifs.length > 0;
                const normRef = groupe.regles_resolues.find(r => r.norm_ref)?.norm_ref || null;
                const titreGroupe = groupe.solutions.length === 1
                  ? groupe.solutions[0]
                  : `Solutions concernées : ${groupe.solutions.join(", ")}`;
                return (
                  <div key={i} style={{
                    marginBottom:18, paddingLeft:12,
                    borderLeft:`3px solid ${C.border}`,
                  }}>
                    {/* Titre : nom unique ou liste groupée */}
                    <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:6 }}>
                      {titreGroupe}
                    </div>

                    {/* Niveau 1 — regles_resolues */}
                    {hasRR && (
                      <>
                        <div style={{ fontSize:12, color:"#4b5563", marginBottom:4 }}>
                          <span style={{ fontWeight:600, color:"#374151" }}>Règle d'éligibilité : </span>
                          {groupe.regles_resolues.map(r => r.id).join(", ")}
                        </div>
                        <div style={{ fontSize:12, color:"#4b5563", marginBottom:4 }}>
                          <span style={{ fontWeight:600, color:"#374151" }}>Justification : </span>
                          {groupe.regles_resolues.map(r => r.raison).filter(Boolean).join(" ")}
                        </div>
                        {normRef && (
                          <div style={{ fontSize:12, color:"#4b5563", fontStyle:"italic" }}>
                            <span style={{ fontWeight:600, color:"#374151", fontStyle:"normal" }}>Référence : </span>
                            {normRef}
                          </div>
                        )}
                      </>
                    )}

                    {/* Niveau 2 — regles + motifs */}
                    {!hasRR && hasRegl && (
                      <div style={{ fontSize:12, color:"#4b5563", marginBottom:4 }}>
                        <span style={{ fontWeight:600, color:"#374151" }}>Règles d'éligibilité : </span>
                        {groupe.regles.join(", ")}
                      </div>
                    )}
                    {!hasRR && hasMotf && (
                      <div style={{ fontSize:12, color:"#4b5563" }}>
                        <span style={{ fontWeight:600, color:"#374151" }}>Justification : </span>
                        {groupe.motifs.join(" ")}
                      </div>
                    )}

                    {/* Niveau 3 — repli ultime */}
                    {!hasRR && !hasRegl && !hasMotf && (
                      <div style={{ fontSize:12, color:C.gris, fontStyle:"italic" }}>
                        Solution non retenue pour ce contexte géotechnique.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ══ SECTION 7 — CONCLUSION OPÉRATIONNELLE ═══════════ */}
        {best && (
          <div style={{ marginTop:24, marginBottom:24, pageBreakInside:"avoid" }}>
            <SectionTitre num="7" titre="Conclusion opérationnelle" />

            {/* §7.1 — Solution recommandée */}
            <div style={{ marginBottom:14, fontSize:12, color:"#374151", lineHeight:1.7 }}>
              Au terme de cette analyse préliminaire,{" "}
              <strong style={{ color:C.bleu }}>{best.nom}</strong>{" "}
              est identifiée comme la solution la plus adaptée au contexte géotechnique du projet
              ({labelOuvrage}{hauteur ? `, H = ${hauteur} m` : ""}).
              {formatNiveau(best.niveau) && (
                <> Niveau d'adéquation retenu : <strong>{formatNiveau(best.niveau)}</strong>.</>
              )}
            </div>

            {/* §7.2 — Logiciels GEO5 */}
            {(() => {
              const modulesUniques = [...new Set(best.modules_geo5 || [])];
              if (modulesUniques.length === 0) return null;
              return (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:6 }}>
                    Logiciels GEO5 mobilisables pour le dimensionnement
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {modulesUniques.map(m => (
                      <span key={m} style={{
                        background:C.grisCl, border:`1px solid ${C.border}`,
                        borderRadius:99, padding:"3px 12px",
                        fontSize:11, color:C.bleu, fontWeight:600,
                      }}>{m}</span>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* §7.3 — Vérifications obligatoires par type d'ouvrage */}
            {(() => {
              let texteVerif, normeVerif;
              if (typeOuvrage?.startsWith("fondation")) {
                texteVerif = "Justification de la portance des fondations et vérification des tassements différentiels";
                normeVerif = "NF P94-261 / EC7 DA2*";
              } else if (typeOuvrage?.startsWith("soutenement")) {
                texteVerif = "Calcul des poussées et butées des terres, vérification de la stabilité de l'écran et des déplacements admissibles";
                normeVerif = "NF P94-282 / EC7 DA2*";
              } else if (typeOuvrage === "stabilisation_talus" || typeOuvrage === "talus_stabilisation") {
                texteVerif = "Analyse de stabilité globale et locale du talus, vérification des mécanismes de glissement les plus défavorables";
                normeVerif = "NF EN 1997-1 (Eurocode 7) / NF EN 14475";
              } else if (typeOuvrage === "amelioration_sol") {
                texteVerif = "Justification de l'amélioration des paramètres géotechniques et vérification de l'efficacité du traitement";
                normeVerif = "NF EN 1997-1 / Recommandations CFMS";
              } else if (typeOuvrage === "infrastructure_lineaire") {
                texteVerif = "Étude de portance des couches de forme et de fondation, vérification des déformations admissibles";
                normeVerif = "NF EN 1997-1 / NF P94-261 / NF P94-262";
              } else {
                texteVerif = "Vérification géotechnique adaptée au type d'ouvrage";
                normeVerif = "NF P94-500 / EC7 DA2*";
              }
              return (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:4 }}>
                    Vérifications géotechniques à prévoir en phase G2 PRO
                  </div>
                  <div style={{ fontSize:12, color:"#374151", lineHeight:1.7 }}>
                    {texteVerif} — à prévoir en phase G2 PRO.{" "}
                    <span style={{ color:C.gris, fontSize:11 }}>({normeVerif})</span>
                  </div>
                </div>
              );
            })()}

            {/* §7.4 — Mission géotechnique requise */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:4 }}>
                Mission géotechnique requise
              </div>
              <div style={{ fontSize:12, color:"#374151", lineHeight:1.7 }}>
                {diagnosticBackend?.rapport?.mission_g2?.texte_complet ||
                  "La mise en œuvre de la solution identifiée nécessite une mission géotechnique G2 " +
                  "(avant-projet et projet), comprenant des reconnaissances de terrain en phase G2 AVP " +
                  "(sondages géotechniques, essais in situ) et un dimensionnement complet en phase G2 PRO, " +
                  "conformément à la norme NF P94-500. Les essais de laboratoire seront conduits selon " +
                  "la norme ISO 17892. Le dimensionnement final doit respecter l'approche de calcul EC7 DA2*."}
              </div>
            </div>

            {/* §7.5 — Conditions de réévaluation */}
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:C.bleu, marginBottom:4 }}>
                Conditions de réévaluation
              </div>
              <div style={{ fontSize:12, color:"#374151", lineHeight:1.7 }}>
                Ce classement est établi sur la base des paramètres saisis. Toute modification des
                conditions de projet — nature du sol, hauteur d'ouvrage, niveau de nappe, contraintes
                d'emprise ou exigences sismiques — est susceptible de conduire à une révision du
                classement et de la solution recommandée. Une mise à jour de l'analyse est recommandée
                avant tout engagement en phase étude.
              </div>
            </div>
          </div>
        )}

        {/* ══ SECTION 8 — DIALOGUE EXPERT SÉLECTIONNÉ ═════════ */}
        {(donneesProjet?.dialogueExpertRapport?.length > 0) && (sectionsActives?.dialogue_expert !== false) && (
          <div style={{ marginTop:24, marginBottom:24, pageBreakInside:"avoid" }}>
            <SectionTitre num="8" titre="Dialogue expert sélectionné" />
            {donneesProjet.dialogueExpertRapport.map((entry, i) => (
              <div
                key={entry.dateAjout + '_' + i}
                style={{
                  position:"relative",
                  border:"1px solid #d1d5db", borderRadius:6,
                  padding:"12px 16px 12px 16px", marginBottom:12,
                  background:"#fafafa",
                }}
              >
                <button
                  className="no-print"
                  onClick={() => onSupprDialogueRapport(i)}
                  style={{
                    position:"absolute", top:8, right:8,
                    background:"transparent", border:"none",
                    fontSize:16, color:"#6b7280", cursor:"pointer", lineHeight:1,
                  }}
                  title="Retirer du rapport"
                >
                  ×
                </button>

                <div style={{ fontSize:12, marginBottom:6 }}>
                  <strong>Solution :</strong>{' '}
                  {entry.solutionNom}
                  {entry.niveauAdequation && (
                    <span style={{
                      marginLeft:8, fontSize:11,
                      padding:"2px 6px", background:"#f3f4f6", borderRadius:3,
                    }}>
                      {entry.niveauAdequation}
                    </span>
                  )}
                </div>

                <div style={{ fontSize:12, marginBottom:6 }}>
                  <strong>Question :</strong>{' '}
                  {entry.question}
                </div>

                <div style={{ fontSize:12, whiteSpace:"pre-wrap" }}>
                  <strong>Réponse synthétique :</strong>{' '}
                  {nettoyerReponsePourRapport(entry.reponse)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ AVERTISSEMENT RÉGLEMENTAIRE ══════════════════════ */}
        <div style={{
          marginTop:24, padding:"12px 16px",
          border:"1px solid #d1d5db", borderRadius:4,
          background:"#f9fafb",
          pageBreakInside:"avoid",
        }}>
          <div style={{ fontSize:13, fontWeight:600, color:"#374151", marginBottom:8 }}>
            Avertissement réglementaire
          </div>
          <p style={{ fontSize:12, lineHeight:1.5, margin:0, color:"#4b5563" }}>
            Ce rapport constitue une aide à la décision préliminaire. Le dimensionnement définitif
            doit être réalisé par un ingénieur géotechnicien qualifié, sur la base d'une mission
            géotechnique adaptée.
          </p>
        </div>

      </div>

      {/* ─── CSS impression ─────────────────────────────────── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body * { visibility: hidden; }
          #rapport-contenu,
          #rapport-contenu * { visibility: visible; }
          #rapport-contenu {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            border: none !important;
            box-shadow: none !important;
            padding: 20mm !important;
            max-width: 100% !important;
          }
          button { display: none !important; }
          @page { margin: 15mm; size: A4; }
        }
      `}</style>
    </div>
  );
}

export default RapportProjet;
